import { useState } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import { Download, GridOn, Description, PictureAsPdf } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { exportApi, type ExportFormat } from '../api/exports.api';

interface Props {
  /** Export dataset key, e.g. 'clients', 'installations', 'service-tickets'. */
  dataset: string;
  /** Permission code gating the export, e.g. 'client.export'. */
  permission: string;
  /** Current filters to apply to the export (must match the on-screen list). */
  filters?: Record<string, unknown>;
  /** Formats to offer (default: Excel, CSV, PDF). */
  formats?: ExportFormat[];
  size?: 'small' | 'medium' | 'large';
  label?: string;
}

const FORMAT_META: Record<ExportFormat, { label: string; icon: JSX.Element }> = {
  xlsx: { label: 'Excel (.xlsx)', icon: <GridOn fontSize="small" /> },
  csv: { label: 'CSV (.csv)', icon: <Description fontSize="small" /> },
  pdf: { label: 'PDF (.pdf)', icon: <PictureAsPdf fontSize="small" /> },
};

/**
 * RBAC-gated export control. Renders NOTHING when the user lacks `permission`
 * (so restricted datasets like Vendor/Technician show no button for normal
 * users). Downloads respect the supplied filters. The backend independently
 * enforces the same permission and audits every export.
 */
export function ExportMenu({ dataset, permission, filters = {}, formats = ['xlsx', 'csv', 'pdf'], size = 'small', label = 'Export' }: Props) {
  const { hasPermission } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);

  if (!hasPermission(permission)) return null;

  const run = async (format: ExportFormat): Promise<void> => {
    setAnchor(null);
    setBusy(true);
    try {
      await exportApi.download(dataset, format, filters);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant="outlined"
        startIcon={busy ? <CircularProgress size={16} /> : <Download />}
        disabled={busy}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        {label}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {formats.map((f) => (
          <MenuItem key={f} onClick={() => run(f)}>
            <ListItemIcon>{FORMAT_META[f].icon}</ListItemIcon>
            <ListItemText>{FORMAT_META[f].label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
